import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import {
  createProcess,
  CreateProcessInput,
  listProcessAgentOptions,
  listProcessSkillOptions,
  listProcesses,
  ProcessListItem,
  updateProcessEnabled,
} from '@/api/processes-api';
import { listMembers } from '@/api/admin-api';
import { useUser, useXeroConnection } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ProvenanceAttribution } from '@/components/work4me/ProvenanceAttribution';
import { Work4MeProcessCreate } from './Work4MeProcessCreate';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { toast } from 'sonner';

function triggerMetaText(process: ProcessListItem): string {
  if (process.triggerType === 'TAG_APPLIED') {
    return `tagId: ${process.triggerDefinition.tagId ?? 'N/A'}`;
  }
  if (process.triggerType === 'MESSAGE_IMPORTED') {
    const directions = process.triggerDefinition.directions;
    return `directions: ${directions && directions.length > 0 ? directions.join(', ') : 'all'}`;
  }
  if (process.triggerType === 'EMAIL_RECEIVED') {
    return process.triggerDefinition.matchCriteria ? `matches: ${process.triggerDefinition.matchCriteria}` : 'AI-routed by match criteria';
  }
  if (process.triggerType === 'SCHEDULE') {
    const { cron, timezone } = process.triggerDefinition;
    return cron && timezone ? `cron: ${cron} (${timezone})` : 'scheduled';
  }
  if (process.triggerType === 'XERO_EVENT') {
    const { eventCategory, eventTypes } = process.triggerDefinition;
    return `Xero ${eventCategory ?? 'record'}: ${eventTypes && eventTypes.length > 0 ? eventTypes.join(', ') : 'no event types'}`;
  }
  if (process.triggerType === 'DOCUMENT_UPDATED') {
    const { tagGroupId, rootDocumentCode } = process.triggerDefinition;
    return `tags group #${tagGroupId ?? 'N/A'}${rootDocumentCode ? `, scope '${rootDocumentCode}'` : ', any document'}`;
  }
  return process.triggerType;
}

export function Work4MeProcessList() {
  const navigate = useNavigate();
  const [showNewProcessDialog, setShowNewProcessDialog] = useState(false);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const { data: memberContext } = useUser();
  const xeroConnectionQuery = useXeroConnection(memberContext?.selectedMember?.id ?? 0);

  const processesQuery = useQuery({
    queryKey: ['processes'],
    queryFn: listProcesses,
  });

  const processAgentOptionsQuery = useQuery({
    queryKey: ['process-agent-options'],
    queryFn: listProcessAgentOptions,
  });

  const processSkillOptionsQuery = useQuery({
    queryKey: ['process-skill-options'],
    queryFn: listProcessSkillOptions,
  });

  const membersQuery = useQuery({
    queryKey: ['admin-members'],
    queryFn: listMembers,
    enabled: showNewProcessDialog,
  });

  const createProcessMutation = useMutation({
    mutationFn: (input: CreateProcessInput) => createProcess(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['processes'] });
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to create process.'));
    },
  });

  const toggleProcessMutation = useMutation({
    mutationFn: ({ processId, enabled }: { processId: string; enabled: boolean }) => updateProcessEnabled(processId, enabled),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['processes'] });
    },
  });

  const filteredProcesses = useMemo(() => {
    const items = processesQuery.data?.items ?? [];
    const term = search.trim().toLowerCase();
    if (!term) {
      return items;
    }
    return items.filter((process) =>
      process.name.toLowerCase().includes(term)
      || (process.description ?? '').toLowerCase().includes(term)
      || (process.systemAgentName ?? process.agentName ?? '').toLowerCase().includes(term)
    );
  }, [search, processesQuery.data]);

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Processes</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="relative w-[240px] sm:w-[280px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search processes..."
                className="h-9 pl-10"
              />
            </div>
            <Button onClick={() => setShowNewProcessDialog(true)} className="gap-2 h-9 px-3">
              <Plus className="h-4 w-4" />
              Process
            </Button>
          </div>
          </div>
          <p className="text-muted-foreground">
            Configure tag-triggered processes, inspect process runs as content, and review step history.
          </p>
        </div>
      </div>

      {processesQuery.isLoading ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">Loading processes...</div>
      ) : null}
      {processesQuery.isError ? (
        <div className="px-4 py-6 text-sm text-destructive">Unable to load processes.</div>
      ) : null}
      {!processesQuery.data?.processesEnabled ? (
        <div className="px-4 py-3 text-xs text-warning border-b bg-warning-subtle/60">
          {processesQuery.data?.processesDisabledReason
            ? `Process runtime disabled: ${processesQuery.data.processesDisabledReason}`
            : 'Process runtime disabled: queue unavailable.'}
        </div>
      ) : null}
      {!processesQuery.isLoading && !processesQuery.isError && filteredProcesses.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          {search.trim() ? 'No processes match your search.' : 'Nothing here yet — automations you create appear here.'}
        </div>
      ) : null}

      {!processesQuery.isLoading && !processesQuery.isError && filteredProcesses.length > 0 ? (
        <ul className="divide-y">
          {filteredProcesses.map((process) => (
            <li
              key={process.id}
              className="px-4 py-3 transition-colors cursor-pointer hover:bg-muted/30"
              role="button"
              tabIndex={0}
              onClick={() => {
                navigate(`/work4me/processes/${process.id}`);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/work4me/processes/${process.id}`);
                }
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{process.name}</p>
                    <Badge variant="outline">{process.status}</Badge>
                    {process.executionKind === 'DETERMINISTIC' ? <Badge variant="secondary">Automatic</Badge> : null}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{process.description ?? 'No description'}</p>
                  <p className="text-xs text-muted-foreground">
                    {triggerMetaText(process)} · agent: {process.systemAgentName ?? process.agentName ?? '—'}
                    {process.skillIds.length > 0 ? ` · skills: ${process.skillIds.join(', ')}` : ''}
                  </p>
                  <ProvenanceAttribution lastChange={process.lastChange} fallbackDate={process.updatedAt} />
                </div>
                <Switch
                  checked={process.status === 'ENABLED'}
                  onCheckedChange={(checked) => toggleProcessMutation.mutate({ processId: process.id, enabled: checked })}
                  onClick={(event) => event.stopPropagation()}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <Work4MeProcessCreate
        open={showNewProcessDialog}
        onOpenChange={setShowNewProcessDialog}
        memberOptions={(membersQuery.data?.members ?? []).map((member) => ({
          id: member.id,
          label: `${member.firstName} ${member.surname}`.trim() || member.email,
        }))}
        onSubmit={async (values) => {
          await createProcessMutation.mutateAsync(values);
        }}
        isSubmitting={createProcessMutation.isPending}
        agentOptions={processAgentOptionsQuery.data?.items ?? []}
        skillOptions={processSkillOptionsQuery.data?.items ?? []}
        xeroConnectionActive={xeroConnectionQuery.data?.connection?.status === 'ACTIVE'}
      />
    </div>
  );
}

